/**
 * Global Health Website V2 hand-over (docs/globalhealth/WEBSITE_V2_PLAN.md §3.2/§3.3).
 *
 * The static marketing site sends visitors into the app on `/r[/:sponsorId]` with
 * the entry they already chose (`plan`, `weeks`) and the door they came through
 * (`journey`, `goal`); it keeps the partner referral in
 * `localStorage['globalhealth.referral']` ({ id, at }, 90 days — customer rule
 * 2026-10-02: last touch until the first confirmed full payment, see
 * SITE_REFERRAL_TTL_DAYS / SPONSOR_SOURCE_PRECEDENCE / rememberReferral). This
 * file is the app's single reading of that contract — the direct checkout, the
 * magic-link landing and the wizard all go through it.
 */

/** Website plan slug -> the product code the wizard sells. Mirrors the backend's
 *  Funnel::LeadIntent::PLAN_CODES. The retired slug `dna_health` (the $1,999
 *  package, removed from sale 2026-10-02) is read as the SPECIAL — old links keep
 *  working and land on the only DNA product there is. */
export const PLAN_CODE_BY_SLUG: Record<string, string> = {
  weekly:              'GH_CLUB_WEEKLY',
  yearly:              'GH_CLUB_YEARLY',
  practitioner_weekly: 'GH_PRACTITIONER_WEEKLY',
  practitioner_yearly: 'GH_PRACTITIONER_YEARLY',
  dna_health_special:  'GH_DNA_HEALTH_SPECIAL',
  dna_health:          'GH_DNA_HEALTH_SPECIAL',
  // DNA Family SPECIAL (docs/globalhealth/DNA_FAMILY_SPEZIAL_MACHBARKEIT.md):
  // only purchasable with a family invitation token (`family`); without one the
  // direct checkout refuses it, and so does the backend.
  dna_family_special:  'GH_DNA_FAMILY_SPECIAL'
};

/** Slugs that are read as another one (retired -> current). */
const SLUG_ALIASES: Record<string, string> = { dna_health: 'dna_health_special' };

/** The slugs whose duration is a number of prepaid weeks (`weeks`). */
export const WEEKLY_SLUGS = ['weekly', 'practitioner_weekly'];

/** The DNA & Health bundle on sale — rendered as one bundle card, never in the
 *  picker. Only the SPECIAL: the $1,999 GH_DNA_HEALTH was removed from sale
 *  (customer decision 2026-10-02) and survives only as a retired code on old rows. */
export const BUNDLE_PLAN_CODES = ['GH_DNA_HEALTH_SPECIAL'];

/** Every bundle code a stored row may carry, retired ones included — for reading
 *  old memberships, never for offering anything. */
export const ALL_BUNDLE_PLAN_CODES = ['GH_DNA_HEALTH', 'GH_DNA_HEALTH_SPECIAL', 'GH_DNA_FAMILY_SPECIAL'];

/** The DNA Family SPECIAL ($999, no voluntary money-back guarantee): a bundle,
 *  but never on general sale — only for a member holding an accepted family
 *  invitation (the server's `offered_family_codes`). Deliberately NOT in
 *  BUNDLE_PLAN_CODES, which lists what anyone may be offered. */
export const FAMILY_BUNDLE_PLAN_CODES = ['GH_DNA_FAMILY_SPECIAL'];
export const FAMILY_PLAN_SLUG = 'dna_family_special';

export function isFamilyCode(code: string | null | undefined): boolean {
  return !!code && FAMILY_BUNDLE_PLAN_CODES.includes(code);
}

export const JOURNEYS = ['weight', 'longevity', 'performance', 'better_health', 'business'];
const GOAL_FORMAT = /^[a-z_]{1,32}$/;
const MIN_WEEKS = 1;
const MAX_WEEKS = 39;

/** Where the website keeps the referral (owned by the site's referral engine). */
const SITE_REFERRAL_KEY = 'globalhealth.referral';

/*
 * REFERRAL ATTRIBUTION — customer rule 2026-10-02 (WEBSITE_V2_KUNDENAENDERUNGEN
 * A.3): last touch until the first confirmed full payment.
 *
 * The newest referral link wins, across page changes, journeys, language switches,
 * product and duration choice, checkout and account creation. rememberReferral
 * writes every explicit link — path or ?ref — over the tab's and the site's stored
 * value, so a later visit without a ref uses the newest one, and the client sends
 * that newest referral with every purchase call (direct checkout, magic-link start,
 * wizard checkout). The sponsor is locked only by the backend (SponsorAttribution)
 * once a full payment is confirmed — never by visiting, choosing, ordering, typing
 * a name or creating the account. Change the rule HERE, in the site's referral
 * engine and in SponsorAttribution together.
 */

/** Lifetime of the stored referral: 90 days (customer rule 2026-10-01). */
export const SITE_REFERRAL_TTL_DAYS = 90;
const SITE_REFERRAL_TTL_MS = SITE_REFERRAL_TTL_DAYS * 24 * 60 * 60 * 1000;

/** Which sponsor source wins when several are present, first match wins:
 *  the path of a partner link (/r/123), then ?ref=123 (the site's own CTAs), then
 *  what this tab already remembered, then the site's stored referral. The two
 *  explicit sources come first because the link in hand is the newest touch. */
export const SPONSOR_SOURCE_PRECEDENCE = ['path', 'query', 'session', 'site'] as const;
export type SponsorSource = typeof SPONSOR_SOURCE_PRECEDENCE[number];

/** Applies SPONSOR_SOURCE_PRECEDENCE to the candidates (each already validated
 *  or null) — the single place the precedence is evaluated. */
export function resolveSponsor(candidates: Partial<Record<SponsorSource, string | null>>): string | null {
  for (const source of SPONSOR_SOURCE_PRECEDENCE) {
    const id = candidates[source];
    if (id) return id;
  }
  return null;
}
/** The app's own per-tab copy of the sponsor (pre-V2, still read first). */
export const SESSION_SPONSOR_KEY = 'globalhealth.funnel.sponsor_id';
const SESSION_INTENT_KEY = 'globalhealth.funnel.intent';

export interface FunnelIntent {
  plan?:    string | null;
  weeks?:   number | null;
  journey?: string | null;
  goal?:    string | null;
}

/** Validates the raw query values; anything invalid is dropped (never an error —
 *  these are marketing parameters and must not stop a visitor). */
export function normaliseIntent(raw: { [key: string]: string | null | undefined }): FunnelIntent {
  const plan = (raw['plan'] ?? '').trim().toLowerCase();
  const journey = (raw['journey'] ?? '').trim().toLowerCase();
  const goal = (raw['goal'] ?? '').trim().toLowerCase();
  const weeks = Number.parseInt((raw['weeks'] ?? '').trim(), 10);

  const intent: FunnelIntent = {};
  if (PLAN_CODE_BY_SLUG[plan]) intent.plan = SLUG_ALIASES[plan] ?? plan;
  if (WEEKLY_SLUGS.includes(intent.plan ?? '') && Number.isInteger(weeks) && weeks >= MIN_WEEKS && weeks <= MAX_WEEKS) {
    intent.weeks = weeks;
  }
  if (JOURNEYS.includes(journey)) intent.journey = journey;
  if (GOAL_FORMAT.test(goal)) intent.goal = goal;
  return intent;
}

/** Remembers the intent for this tab, so it survives the check-email detour. A
 *  new, non-empty intent replaces the old one — it is what the visitor chose last. */
export function storeIntent(intent: FunnelIntent): void {
  if (!Object.keys(intent).length) return;
  try { sessionStorage.setItem(SESSION_INTENT_KEY, JSON.stringify(intent)); } catch { /* storage blocked */ }
}

export function readStoredIntent(): FunnelIntent {
  try {
    const raw = sessionStorage.getItem(SESSION_INTENT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) ?? {};
    return normaliseIntent({
      plan: parsed.plan, weeks: parsed.weeks != null ? String(parsed.weeks) : null,
      journey: parsed.journey, goal: parsed.goal
    });
  } catch {
    return {};
  }
}

/** The stored referral (latest touch), or null when absent, malformed, "0" or
 *  older than SITE_REFERRAL_TTL_DAYS. */
export function readSiteReferral(now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(SITE_REFERRAL_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { id?: string | number; at?: number };
    const id = String(entry?.id ?? '').trim();
    if (!/^\d+$/.test(id) || id === '0') return null;
    if (typeof entry.at !== 'number' || now - entry.at > SITE_REFERRAL_TTL_MS) return null;
    return id;
  } catch {
    return null;
  }
}

/** A sponsor id as the funnel accepts it: digits, not "0". */
export function validSponsorId(value: string | null | undefined): string | null {
  const id = (value ?? '').trim();
  return /^\d+$/.test(id) && id !== '0' ? id : null;
}

/**
 * Last touch: an explicit referral link (path or ?ref) replaces whatever this tab
 * and the site remembered, with a fresh 90-day lifetime — so the visitor's next
 * visit without a ref still credits the newest link. Same entry format the site's
 * referral engine writes ({ id, at }), same origin (both served by the GH nginx).
 */
export function rememberReferral(id: string, now = Date.now()): void {
  const sponsor = validSponsorId(id);
  if (!sponsor) return;
  try { sessionStorage.setItem(SESSION_SPONSOR_KEY, sponsor); } catch { /* storage blocked */ }
  try {
    localStorage.setItem(SITE_REFERRAL_KEY, JSON.stringify({ id: sponsor, at: now }));
  } catch { /* storage blocked */ }
}

/**
 * The newest referral this browser holds, without a referral link in hand: what
 * this tab remembered, then the site's stored referral (SPONSOR_SOURCE_PRECEDENCE).
 * Sent as `sponsor_id` with every purchase call — the backend decides whether it
 * still applies (the sponsor is locked at the first confirmed full payment).
 */
export function currentReferral(query?: string | null): string | null {
  let fromSession: string | null = null;
  try { fromSession = validSponsorId(sessionStorage.getItem(SESSION_SPONSOR_KEY)); } catch { /* storage blocked */ }
  return resolveSponsor({ query: validSponsorId(query), session: fromSession, site: readSiteReferral() });
}

/** The wizard URL (without the language prefix) for a plan code + weeks, marked as
 *  preselected so the wizard skips its plan step. */
export function wizardQuery(code: string, weeks?: number | null): Record<string, string | number> {
  return {
    category: 'club',
    plan: code,
    ...(weeks ? { weeks } : {}),
    preselected: 1
  };
}

// ----- Family invitation token ------------------------------------------------
// The token of a DNA Family SPECIAL invitation (/family/join/<token>), kept for
// this tab so it survives the direct checkout, the sign-in detour and the
// magic-link landing — mirrors the sponsor's session copy (rememberReferral).
// Cleared once the invitation was accepted (bound to the account).

const SESSION_FAMILY_TOKEN_KEY = 'globalhealth.funnel.family_token';
/** 32 random bytes, URL-safe base64 (no padding) — loosely checked: the server decides. */
const FAMILY_TOKEN_FORMAT = /^[A-Za-z0-9_-]{16,128}$/;

export function validFamilyToken(value: string | null | undefined): string | null {
  const token = (value ?? '').trim();
  return FAMILY_TOKEN_FORMAT.test(token) ? token : null;
}

export function rememberFamilyToken(value: string | null | undefined): void {
  const token = validFamilyToken(value);
  if (!token) return;
  try { sessionStorage.setItem(SESSION_FAMILY_TOKEN_KEY, token); } catch { /* storage blocked */ }
}

/** The token in hand (`query`) wins over the one this tab remembered. */
export function currentFamilyToken(query?: string | null): string | null {
  const explicit = validFamilyToken(query);
  if (explicit) return explicit;
  try { return validFamilyToken(sessionStorage.getItem(SESSION_FAMILY_TOKEN_KEY)); } catch { return null; }
}

export function clearFamilyToken(): void {
  try { sessionStorage.removeItem(SESSION_FAMILY_TOKEN_KEY); } catch { /* storage blocked */ }
}

/** "I already have an account": the family landing accepts the invitation on its
 *  own once the visitor is back from the sign-in, for exactly this token. */
const FAMILY_AUTO_ACCEPT_KEY = 'globalhealth.funnel.family_auto_accept';

export function markFamilyAutoAccept(token: string): void {
  try { sessionStorage.setItem(FAMILY_AUTO_ACCEPT_KEY, token); } catch { /* storage blocked */ }
}

/** True (once) when the visitor was sent to sign in to accept this token. */
export function consumeFamilyAutoAccept(token: string): boolean {
  try {
    const marked = sessionStorage.getItem(FAMILY_AUTO_ACCEPT_KEY);
    if (marked !== token) return false;
    sessionStorage.removeItem(FAMILY_AUTO_ACCEPT_KEY);
    return true;
  } catch {
    return false;
  }
}

// ----- "Sign in and come back to this purchase" -------------------------------
// The login page sends a signed-in user to RouteService's `homeUrl`. To return a
// visitor with an existing account to the purchase they started, the purchase URL
// is put there for one sign-in, and the previous home is restored by the wizard
// once it has been reached (consumeLoginReturn).

const LOGIN_RETURN_KEY = 'globalhealth.checkout.login_return';

export function returnHereAfterLogin(target: string): void {
  try {
    const previous = localStorage.getItem('homeUrl');
    sessionStorage.setItem(LOGIN_RETURN_KEY, JSON.stringify({ previous }));
    localStorage.setItem('homeUrl', target);
  } catch { /* storage blocked — the login page then lands on the dashboard */ }
}

export function consumeLoginReturn(): void {
  try {
    const raw = sessionStorage.getItem(LOGIN_RETURN_KEY);
    if (!raw) return;
    sessionStorage.removeItem(LOGIN_RETURN_KEY);
    const previous = (JSON.parse(raw) ?? {}).previous as string | null;
    if (previous) localStorage.setItem('homeUrl', previous);
    else localStorage.removeItem('homeUrl');
  } catch { /* ignore */ }
}
